<template>
  <div class="version-history-container">
    <a-page-header 
      :title="`版本历史 - ${knowledgeName}`" 
      @back="() => $router.back()"
    >
      <template #extra>
        <a-space>
          <router-link :to="{ name: 'knowledge-edit', params: { id: $route.params.id } }">
            <a-button type="link">
              <EditOutlined /> 编辑知识
            </a-button>
          </router-link>
          <a-divider type="vertical" />
          <a-button @click="handleCompare" :disabled="selectedVersions.length !== 2">
            对比选中版本 ({{ selectedVersions.length }}/2)
          </a-button>
        </a-space>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <a-table
        :columns="columns"
        :data-source="versions"
        :loading="loading"
        row-key="version"
        :row-selection="rowSelection"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'version'">
            <a-space>
              <span>v{{ record.version }}</span>
              <a-tag v-if="record.is_latest" color="blue">预发布环境</a-tag>
              <a-tag v-if="record.is_published" color="green">生产环境</a-tag>
            </a-space>
          </template>
          <template v-else-if="column.key === 'commit_message'">
            {{ record.commit_message || '-' }}
          </template>
          <template v-else-if="column.key === 'create_time'">
            {{ formatDate(record.create_time) }}
          </template>
          <template v-else-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="handleView(record)">
                查看
              </a-button>
              <a-popconfirm
                v-if="!record.is_published"
                title="确定要将此版本发布到生产环境吗？"
                @confirm="handlePublish(record)"
              >
                <a-button type="link" size="small">发布到生产环境</a-button>
              </a-popconfirm>
              <span v-else style="color: #999; padding: 0 8px;">-</span>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 版本详情弹窗 -->
    <a-modal
      v-model:open="detailVisible"
      :title="`版本 v${currentVersion?.version || ''} 详情`"
      width="800px"
      :footer="null"
    >
      <a-descriptions bordered :column="2" v-if="currentVersionDetail">
        <a-descriptions-item label="标题" :span="2">
          {{ currentVersionDetail.title }}
        </a-descriptions-item>
        <a-descriptions-item label="知识类型">
          {{ currentVersionDetail.type_name }}
        </a-descriptions-item>
        <a-descriptions-item label="内容格式">
          {{ currentVersionDetail.content_format }}
        </a-descriptions-item>
        <a-descriptions-item label="版本说明" :span="2">
          {{ currentVersionDetail.commit_message || '-' }}
        </a-descriptions-item>
        <a-descriptions-item label="创建时间">
          {{ formatDate(currentVersionDetail.create_time) }}
        </a-descriptions-item>
        <a-descriptions-item label="排序">
          {{ currentVersionDetail.sort_order }}
        </a-descriptions-item>
        <a-descriptions-item label="关联标签" :span="2">
          <template v-if="currentVersionDetail.tags && currentVersionDetail.tags.length > 0">
            <a-tag v-for="tag in currentVersionDetail.tags" :key="tag.id">
              {{ tag.tag_value || tag.tag_name }}
            </a-tag>
          </template>
          <span v-else>-</span>
        </a-descriptions-item>
        <a-descriptions-item label="关联步骤" :span="2">
          <template v-if="currentVersionDetail.steps && currentVersionDetail.steps.length > 0">
            <a-tag v-for="step in currentVersionDetail.steps" :key="step.step_order">
              步骤{{ step.step_order }}
            </a-tag>
          </template>
          <span v-else>全部步骤</span>
        </a-descriptions-item>
        <a-descriptions-item label="内容" :span="2">
          <pre style="max-height: 300px; overflow: auto; background: #f5f5f5; padding: 12px; border-radius: 4px; white-space: pre-wrap;">{{ currentVersionDetail.content || '-' }}</pre>
        </a-descriptions-item>
      </a-descriptions>
    </a-modal>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import { EditOutlined } from '@ant-design/icons-vue';
import { getKnowledgeById, getKnowledgeVersionHistory, getKnowledgeVersionDetail, publishKnowledgeVersion } from '../api/knowledge';

export default {
  name: 'KnowledgeVersionHistory',
  components: {
    EditOutlined
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const knowledgeName = ref('');
    const versions = ref([]);
    const selectedVersions = ref([]);
    const detailVisible = ref(false);
    const currentVersion = ref(null);
    const currentVersionDetail = ref(null);

    const columns = [
      { title: '版本', key: 'version', width: 180 },
      { title: '版本说明', key: 'commit_message', ellipsis: true },
      { title: '创建时间', key: 'create_time', width: 180 },
      { title: '操作', key: 'action', width: 150 }
    ];

    // 行选择配置
    const rowSelection = computed(() => ({
      selectedRowKeys: selectedVersions.value,
      onChange: (keys) => {
        // 限制最多选择2个版本
        if (keys.length <= 2) {
          selectedVersions.value = keys;
        } else {
          // 如果超过2个，只保留最后选择的2个
          selectedVersions.value = keys.slice(-2);
          message.warning('最多选择2个版本进行对比');
        }
      }
    }));

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const loadData = async () => {
      loading.value = true;
      try {
        const knowledgeId = route.params.id;
        const isDeleted = route.query.deleted === '1';
        const titleFromQuery = route.query.title;
        
        // 如果从回收站进入，优先使用传递的标题
        if (titleFromQuery) {
          knowledgeName.value = titleFromQuery;
        }
        
        // 加载版本历史（版本历史不受 is_deleted 影响）
        const versionsRes = await getKnowledgeVersionHistory(knowledgeId);
        versions.value = versionsRes.data || [];
        
        // 从版本历史中获取知识标题
        if (!titleFromQuery && versions.value.length > 0) {
          const latestVersion = versions.value[0];
          knowledgeName.value = latestVersion.title || `知识 #${knowledgeId}`;
        } else if (!titleFromQuery) {
          knowledgeName.value = `知识 #${knowledgeId}`;
        }
        
        // 如果不是从回收站进入，尝试加载知识基本信息
        if (!isDeleted) {
          try {
            const knowledgeRes = await getKnowledgeById(knowledgeId);
            if (knowledgeRes.data?.title) {
              knowledgeName.value = knowledgeRes.data.title;
            }
          } catch (e) {
            // 忽略错误，使用已有的标题
          }
        }
      } catch (error) {
        message.error('加载数据失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    const handleView = async (record) => {
      currentVersion.value = record;
      detailVisible.value = true;
      
      try {
        const res = await getKnowledgeVersionDetail(route.params.id, record.version);
        currentVersionDetail.value = res.data;
      } catch (error) {
        message.error('加载版本详情失败');
      }
    };

    const handlePublish = async (record) => {
      try {
        await publishKnowledgeVersion(route.params.id, record.version);
        message.success(`版本 v${record.version} 发布成功`);
        await loadData();
      } catch (error) {
        message.error('发布失败');
      }
    };

    const handleCompare = () => {
      if (selectedVersions.value.length === 2) {
        const [v1, v2] = selectedVersions.value.sort((a, b) => a - b);
        router.push({
          name: 'knowledge-version-compare',
          params: { id: route.params.id },
          query: { v1, v2 }
        });
      }
    };

    onMounted(() => {
      loadData();
    });

    return {
      loading,
      knowledgeName,
      versions,
      selectedVersions,
      columns,
      rowSelection,
      detailVisible,
      currentVersion,
      currentVersionDetail,
      formatDate,
      handleView,
      handlePublish,
      handleCompare
    };
  }
};
</script>

<style scoped>
.version-history-container {
  padding: 16px;
}
</style>

